import { PDFDocument, rgb, type PDFPage } from 'pdf-lib';
import { getStroke } from 'perfect-freehand';
import type { LoadedDocument } from './document';
import type { DocumentStrokes, Stroke } from './strokes';

// ===== Build (возвращают Blob, не скачивают) =====

export async function buildPdfBlob(doc: LoadedDocument, strokes: DocumentStrokes): Promise<Blob> {
  if (doc.kind === 'pdf') return buildAnnotatedPdfBlob(doc, strokes);
  if (doc.kind === 'rasterized-pdf') return buildRasterizedPdfBlob(doc, strokes);
  return buildImagePdfBlob(doc, strokes);
}

async function buildAnnotatedPdfBlob(
  doc: Extract<LoadedDocument, { kind: 'pdf' }>,
  strokes: DocumentStrokes
): Promise<Blob> {
  const pdfDoc = await PDFDocument.load(doc.data.slice() as unknown as ArrayBuffer);
  const pages = pdfDoc.getPages();
  for (let i = 0; i < pages.length; i++) {
    const list = strokes[i] ?? [];
    if (!list.length) continue;
    const page = pages[i];
    const { width, height } = page.getSize();
    for (const s of list) drawStrokeOnPdfPage(page, s, width, height);
  }
  const bytes = await pdfDoc.save();
  return new Blob([bytes as BlobPart], { type: 'application/pdf' });
}

const JPEG_QUALITY = 0.85;

async function pngBytesToJpegBytes(pngBytes: Uint8Array, quality: number): Promise<Uint8Array> {
  const blob = new Blob([pngBytes as BlobPart], { type: 'image/png' });
  const bmp = await createImageBitmap(blob);
  const canvas = document.createElement('canvas');
  canvas.width = bmp.width;
  canvas.height = bmp.height;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('canvas 2d unavailable');
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  ctx.drawImage(bmp, 0, 0);
  bmp.close?.();
  const jpegBlob: Blob | null = await new Promise((r) =>
    canvas.toBlob((b) => r(b), 'image/jpeg', quality)
  );
  if (!jpegBlob) throw new Error('toBlob jpeg failed');
  return new Uint8Array(await jpegBlob.arrayBuffer());
}

async function buildRasterizedPdfBlob(
  doc: Extract<LoadedDocument, { kind: 'rasterized-pdf' }>,
  strokes: DocumentStrokes
): Promise<Blob> {
  const pdfDoc = await PDFDocument.create();
  for (let i = 0; i < doc.pageCount; i++) {
    const res = await fetch(doc.pageUrls[i], { cache: 'force-cache' });
    if (!res.ok) continue;
    const pngBytes = new Uint8Array(await res.arrayBuffer());
    const jpegBytes = await pngBytesToJpegBytes(pngBytes, JPEG_QUALITY);
    const img = await pdfDoc.embedJpg(jpegBytes);
    const page = pdfDoc.addPage([img.width, img.height]);
    page.drawImage(img, { x: 0, y: 0, width: img.width, height: img.height });
    const list = strokes[i] ?? [];
    for (const s of list) drawStrokeOnPdfPage(page, s, img.width, img.height);
  }
  const bytes = await pdfDoc.save();
  return new Blob([bytes as BlobPart], { type: 'application/pdf' });
}

async function buildImagePdfBlob(
  doc: Extract<LoadedDocument, { kind: 'image' }>,
  strokes: DocumentStrokes
): Promise<Blob> {
  const canvas = document.createElement('canvas');
  canvas.width = doc.imageBitmap.width;
  canvas.height = doc.imageBitmap.height;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('canvas 2d unavailable');
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  ctx.drawImage(doc.imageBitmap, 0, 0);
  const jpegBlob: Blob | null = await new Promise((r) =>
    canvas.toBlob((b) => r(b), 'image/jpeg', JPEG_QUALITY)
  );
  if (!jpegBlob) throw new Error('toBlob jpeg failed');
  const jpegBytes = new Uint8Array(await jpegBlob.arrayBuffer());

  const pdfDoc = await PDFDocument.create();
  const img = await pdfDoc.embedJpg(jpegBytes);
  const page = pdfDoc.addPage([img.width, img.height]);
  page.drawImage(img, { x: 0, y: 0, width: img.width, height: img.height });
  for (const s of strokes[0] ?? []) drawStrokeOnPdfPage(page, s, img.width, img.height);
  const bytes = await pdfDoc.save();
  return new Blob([bytes as BlobPart], { type: 'application/pdf' });
}

// ===== Export (build + download) =====

export async function exportAnnotatedPdf(
  doc: Extract<LoadedDocument, { kind: 'pdf' }>,
  strokes: DocumentStrokes
): Promise<void> {
  const blob = await buildAnnotatedPdfBlob(doc, strokes);
  downloadBlob(blob, suggestName(doc.fileName, '.pdf', '-marked'));
}

export async function exportRasterizedAsPdf(
  doc: Extract<LoadedDocument, { kind: 'rasterized-pdf' }>,
  strokes: DocumentStrokes
): Promise<void> {
  const blob = await buildRasterizedPdfBlob(doc, strokes);
  downloadBlob(blob, suggestName(doc.fileName, '.pdf', '-marked'));
}

export async function exportPagePng(
  pageCanvas: HTMLCanvasElement,
  stageSize: { w: number; h: number },
  strokes: Stroke[],
  fileName: string,
  pageIndex: number
): Promise<void> {
  const dpr = window.devicePixelRatio || 1;
  const out = document.createElement('canvas');
  out.width = Math.floor(stageSize.w * dpr);
  out.height = Math.floor(stageSize.h * dpr);
  const ctx = out.getContext('2d');
  if (!ctx) return;
  ctx.drawImage(pageCanvas, 0, 0, out.width, out.height);
  for (const stroke of strokes) {
    const pts: number[][] = stroke.points.map((p) => [
      p.x * out.width,
      p.y * out.height,
      p.pressure,
    ]);
    const outline = getStroke(pts, {
      size: stroke.size * dpr,
      thinning: 0.55,
      smoothing: 0.6,
      streamline: 0.4,
      simulatePressure: true,
      last: true,
    });
    if (!outline.length) continue;
    ctx.fillStyle = stroke.color;
    ctx.beginPath();
    ctx.moveTo(outline[0][0], outline[0][1]);
    for (let i = 1; i < outline.length; i++) ctx.lineTo(outline[i][0], outline[i][1]);
    ctx.closePath();
    ctx.fill();
  }
  const blob: Blob | null = await new Promise((resolve) =>
    out.toBlob((b) => resolve(b), 'image/png', 1)
  );
  if (!blob) return;
  downloadBlob(blob, suggestName(fileName, '.png', `-page${pageIndex + 1}`));
}

// ===== helpers =====

function drawStrokeOnPdfPage(page: PDFPage, stroke: Stroke, width: number, height: number): void {
  const pts: number[][] = stroke.points.map((p) => [
    p.x * width,
    p.y * height,
    p.pressure,
  ]);
  const sizeScale = width / 800;
  const outline = getStroke(pts, {
    size: stroke.size * sizeScale,
    thinning: 0.55,
    smoothing: 0.6,
    streamline: 0.4,
    simulatePressure: true,
    last: true,
  });
  if (!outline.length) return;
  const { r, g, b } = hexToRgb(stroke.color);
  let d = `M ${outline[0][0]} ${outline[0][1]}`;
  for (let i = 1; i < outline.length; i++) d += ` L ${outline[i][0]} ${outline[i][1]}`;
  d += ' Z';
  page.drawSvgPath(d, {
    color: rgb(r, g, b),
    borderWidth: 0,
    y: height,
  });
}

function hexToRgb(hex: string): { r: number; g: number; b: number } {
  const h = hex.replace('#', '');
  return {
    r: parseInt(h.slice(0, 2), 16) / 255,
    g: parseInt(h.slice(2, 4), 16) / 255,
    b: parseInt(h.slice(4, 6), 16) / 255,
  };
}

function downloadBlob(blob: Blob, fileName: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

function suggestName(original: string, ext: string, suffix: string): string {
  const base = original.replace(/\.[^.]+$/, '');
  return `${base}${suffix}${ext}`;
}
